import React from 'react';

interface AvatarProps {
  name?: string | null;
  src?: string | null;
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  className?: string;
  ringClassName?: string;
}

const SIZE_CLASSES: Record<NonNullable<AvatarProps['size']>, string> = {
  xs: 'w-6 h-6 text-[10px]',
  sm: 'w-8 h-8 text-xs',
  md: 'w-10 h-10 text-sm',
  lg: 'w-12 h-12 text-base',
  xl: 'w-20 h-20 text-2xl',
};

const initialsFrom = (name?: string | null): string => {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  const first = parts[0][0] ?? '';
  const second = parts.length > 1 ? parts[parts.length - 1][0] ?? '' : '';
  return (first + second).toUpperCase() || '?';
};

// Deterministic bg color from name — same person always gets the same tint.
const BG_COLORS = [
  'bg-blue-100 text-blue-700',
  'bg-emerald-100 text-emerald-700',
  'bg-purple-100 text-purple-700',
  'bg-orange-100 text-orange-700',
  'bg-pink-100 text-pink-700',
  'bg-cyan-100 text-cyan-700',
  'bg-amber-100 text-amber-700',
  'bg-indigo-100 text-indigo-700',
];

const colorFor = (name?: string | null): string => {
  if (!name) return BG_COLORS[0];
  let sum = 0;
  for (const ch of name) sum = (sum + ch.charCodeAt(0)) & 0xffff;
  return BG_COLORS[sum % BG_COLORS.length];
};

const Avatar: React.FC<AvatarProps> = ({ name, src, size = 'md', className = '', ringClassName = '' }) => {
  const sizeClass = SIZE_CLASSES[size];
  const base = `${sizeClass} rounded-full overflow-hidden flex items-center justify-center font-semibold flex-shrink-0 ${ringClassName} ${className}`;

  if (src) {
    return (
      <div className={`${base} bg-gray-100`}>
        <img src={src} alt={name || 'avatar'} className="w-full h-full object-cover" />
      </div>
    );
  }

  const bg = colorFor(name);
  return (
    <div className={`${base} ${bg}`} title={name || undefined}>
      {initialsFrom(name)}
    </div>
  );
};

export default Avatar;
